import checkmateApi from "../axios/checkmateApi"
import { IQueryBuilder } from "../slices/querySlice";

export const getQueryList = async (size: number, from: number) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search?size=${size}&from=${from}`
    });
    return response;
}

export const getTaxonomyLookupList = async () => {
    const response = checkmateApi({
        method: 'GET',
        url: 'taxonomy'
    });
    return response;
}

export const getFormBuilderOptions = async (taxonomyString: string) => {
    let url = `search/advance/${taxonomyString}`;
    const response = checkmateApi({
        method: 'GET',
        url
    });
    return response;
}

export const getQueryConstraints = async () => {
    const response = checkmateApi({
        method: 'GET',
        url: 'search/constraintTypes'
    })
    return response;
}

export const getSuggestions = async (queryString: string, taxonomyString: string) => {
    let url = `search/suggest?q=${queryString}`
    if (taxonomyString) {
        url += `&index=${taxonomyString?.toLowerCase()}_search_index`
    }
    const response = checkmateApi({
        method: 'GET',
        url
    });
    return response;
}

export const getQueryParameters = async (correlationId: string) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search/${correlationId}`
    });
    return response;
}

export const createAdvancedQuery = async (payload: IQueryBuilder) => {
    const { expand, size, from, operatorBoolean, labels, name, ...data} = payload;
    const response = checkmateApi({
        method: 'POST',
        url: `search/advance?expand=${expand}&size=${size}&from=${from || 0}`,
        data: {
            ...data,
            expanded: expand,
            ...(name ? { name } : {})
        }
    });
    return response;
}

// Placeholder contract — see tech-spec-CHKM-3973 §5.5/§14 OQ-2. Isolated to this function so the
// backend's eventual search/advance/dsl request/response shape is a one-function change.
export const createDslQuery = async (payload: {
    queryType: string;
    queryDepth: number;
    dsl: string;
    name?: string;
    size?: number;
    from?: number;
    expand?: boolean;
}) => {
    const { queryType, queryDepth, dsl, name, size = 1000, from = 0, expand = false } = payload;
    const response = checkmateApi({
        method: 'POST',
        url: `search/advance/dsl?expand=${expand}&size=${size}&from=${from}`,
        data: { dsl, queryType, queryDepth, filters: [], ...(name ? { name } : {}) }
    });
    return response;
};

export const createQuery = async (payload: IQueryBuilder) => {
    const { searchQuery, queryType, expand, queryDepth, size } = payload;
    let url = `search/query?q=${searchQuery}&expand=${expand}&size=${size}&depth=${queryDepth}`;
    if (queryType) {
        url += `&index=${queryType?.toLowerCase()}_search_index`
    }
    const response = checkmateApi({
        method: 'GET',
        url
    });
    return response;
}

export const createBatchQuery = async (payload: { searches: any[] }) => {
    const response = checkmateApi({
        method: 'POST',
        url: `search/batch`,
        data: payload
    });
    return response;
}

export const uploadSupplementalData = async (payload: { file: File }) => {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = checkmateApi({
        method: 'POST',
        url: `document/upload`,
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response;
}

export const getSupplementalDataInfo = async (entityId: string) => {
    const response = checkmateApi({
        method: 'GET',
        url: `document/upload/${entityId}?index=false`
    });
    return response;
}

export const getDashboardData = async (limit = 25) => {
    const response = checkmateApi({
        method: 'GET',
        url: `dashboards?limit=${limit}`,
    });
    return response;
}

export const getATAIResponse = async (payload: { input: string, type: string }) => {
    const response = checkmateApi({
        method: 'POST',
        url: `prompt/direct`,
        data: payload,
    });
    return response;
}

export const deleteDocument = async (id: string) => {
    const response = checkmateApi({
        method: 'DELETE',
        url: `document/${id}`,
    });
    return response;
}

export const getChatHistory = async () => {
    const response = checkmateApi({
        method: 'GET',
        url: `prompt/history`
    });
    return response;
}

export const getJoinedResults = async (data: {
    ids: string[];
    operator: string;
    depth: number;
    searchQuery: string;
    taxonomyType: string;
    filters: {
        key: string;
        constraint: string;
        value: string;
    }[];
}) => {
    const response = checkmateApi({
        method: 'POST',
        url: `search/result`,
        data
    });
    return response;
}

export const getQueryResults = async (entityId: string) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search/result/${entityId}`
    });
    // const response = await timer({
    //     data: mockResultsData
    // }, 50);
    return response;
}

export const getEntitySummaryGraph = async (entityId: string, entityType: string) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search/${entityType.toLowerCase()}/result?ids=${entityId}&depth=1`
    });
    return response;
}

export const getExpansionData = async (entityId: string, entityType: string, depth: number) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search/${entityType.toLowerCase()}/result/${entityId}?depth=${depth}`
    });
    return response;
}

export const getGraphPreview = async (entityId: string, entityType: string, signal?: AbortSignal) => {
    const response = checkmateApi({
        method: 'GET',
        url: `search/${entityType.toLowerCase()}/result/${entityId}`,
        signal,
    });
    return response;
}

export const saveGraphState = async (payload: { rootIds: string[], excluded: string[], extended: string[], name: string, correlationId: string }) => {
    const { correlationId, ...data } = payload;
    const response = checkmateApi({
        method: 'PUT',
        url: `search/${correlationId}`,
        data
    });
    return response;
}

// export const getChartDataMock = async () => {
//     const response = await timer({
//         data: transformNeo4JToCytoscape(mockChartData)
//     }, 50);
//     return response;
// }

export const deleteSearch = async (entityId: string) => {
    const response = await checkmateApi({
        method: 'DELETE',
        url: `search/${entityId}`,
    });
    return response;
}

export const bulkDeleteSearch = async (searchIds: string) => {
    const encodedIds = encodeURIComponent(searchIds);
    const response = await checkmateApi({
        method: 'DELETE',
        url: `search/list?ids=${encodedIds}`
    });
    return response;
}

export const addSearchTag = async (payload: any) => {
    const response = await checkmateApi({
        url: '/search/searchtag',
        method: 'POST',
        data: payload
    });
    return response;
}

export const getSearch = async (searchId: string) =>{
    const response = await checkmateApi({
        url: `/search/list?ids=${searchId}`,
        method: `GET`
    });
    return response
}

export const deleteSearchTag = async (correlationId: string, entityId: string) => {
    const response = await checkmateApi({
        url: `/search/searchtag/search/${correlationId}/entity/${entityId}`,
        method: 'DELETE'
    });
    return response;
}

export const getSearchStatus = async (correlationId: string) => {
    const response = await checkmateApi({
        url: `/search/${correlationId}/status`,
        method: 'GET'
    });
    return response;
}

export const disambiguateEntity = async (payload: { searchId: string }) => {
    const { searchId } = payload;
    const response = await checkmateApi({
        url: `/search/disambiguate/${searchId}`,
        method: 'GET'
    });
    return response;
}

interface RootIdItem {
    id: string;
    score: number;
}


export const updateRootIds = async (payload: { selectedRows: any[], hasTargets: boolean, rootIds: string[] | RootIdItem[], searchId: string }) => {
    const { searchId, rootIds, hasTargets, selectedRows } = payload;
    
    const formattedRootIds: RootIdItem[] = rootIds.map(item => 
        typeof item === 'string' ? { id: item, score: 100 } : item
    );
    

    const patchOperations = [
        {
            op: "replace",
            path: "/rootIds",
            value: formattedRootIds
        },
        {
            op: "replace",
            path: "/targetsResolved",
            value: true
        },
        ...(!hasTargets ? [
            {
                op: "replace",
                path: "/expanded",
                value: false
            },
            {
                op: "replace",
                path: "/status",
                value: "COMPLETE"
            },
            {
                op: "replace",
                path: "/searchType",
                value: "result"
            },
            {
                op: "replace",
                path: "/nodes",
                value: selectedRows
            }
        ] : [])
    ];
    
    const response = await checkmateApi({
        url: `/search/result/${searchId}`,
        method: 'PATCH',
        data: patchOperations,
        headers: {
            'Content-Type': 'application/json-patch+json'
        }
    });
    return response;
}

export const getRootIds = async (payload: { searchIds: string[] }) => {
    const { searchIds } = payload;
    const response = await checkmateApi({
        url: `/search/roots/list?ids=${searchIds.join(',')}`,
        method: 'GET'
    });
    return response;
}

export const elasticSearch = async (payload: { indexName: string, mappingContext: Record<string, string> }) => {
    const response = await checkmateApi({
        method: 'POST',
        url: '/elasticsearch/query/execute',
        data: payload
    });
    return response;
}